import React, { FC } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Order } from '../../../common/model/dto/order/order';
import { OrderDataUtils } from '../../../common/utils/services/order-data-utils';
import {
  getFulfillmentStatusTone,
  getOrderStatusTone,
  getPaymentStatusTone,
} from '../../../common/utils/services/order-status-utils';
import breakpoints from '../../../design-system/breakpoints';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import OvBackButton from '../atoms/OvBackButton';
import OvLabelledField from '../atoms/OvLabelledField';
import OvStatusChip from '../atoms/OvStatusChip';
import OvCopyToClipboardButton from '../molecules/OvCopyToClipboardButton';
import OvOrderDeliveryStatus from '../molecules/OvOrderDeliveryStatus';
import OvOrderHeaderActions from '../molecules/OvOrderHeaderActions';
import OvRecordDetailsHeader from './OvRecordDetailsHeader';

interface OvOrderDetailsHeaderProps {
  order: Order;
  isUpdateAllowed: boolean;
  isActionDisabled: boolean;
  onEditStatus: () => void;
  onEditPaymentStatus: () => void;
  onSubmit: () => void;
}

const OvOrderDetailsHeader: FC<OvOrderDetailsHeaderProps> = ({
  order,
  isUpdateAllowed,
  isActionDisabled,
  onEditStatus,
  onEditPaymentStatus,
  onSubmit,
}) => {
  const { t } = useTranslation();

  return (
    <OvRecordDetailsHeader
      breadcrumbLabel={t('orders.details.breadcrumb')}
      backControl={<OvBackButton label={t('app.subPages.menu.orders')} />}
      current={`#${order.order_number}`}
      title={t('orders.details.title', { number: order.order_number })}
      titleAside={
        <Amount>
          {OrderDataUtils.formatAmount(OrderDataUtils.getTotal(order))}
        </Amount>
      }
      meta={
        <>
          {order.internal_id && (
            <span>
              {order.internal_id}
              <OvCopyToClipboardButton
                text={order.internal_id}
                label={t('orders.details.copyInternalId')}
              />
            </span>
          )}
          {order.ordered_at && (
            <span>
              {t('orders.details.orderedOn', {
                date: OrderDataUtils.formatDateTime(
                  order.ordered_at,
                  'DD MMM YYYY'
                ),
              })}
            </span>
          )}
          {order.impact_click_id && <Tag>{t('orders.affiliate')}</Tag>}
          {order.tags?.map((tag) => (
            <Tag key={tag}>{tag}</Tag>
          ))}
        </>
      }
      actions={
        isUpdateAllowed && (
          <OvOrderHeaderActions
            isSubmitted={order.is_submitted}
            isDisabled={isActionDisabled}
            onEditStatus={onEditStatus}
            onEditPaymentStatus={onEditPaymentStatus}
            onSubmit={onSubmit}
          />
        )
      }
    >
      <StatusRail>
        <OvLabelledField label={t('orders.tableHeaders.orderStatus')}>
          <OvStatusChip
            label={order.status}
            tone={getOrderStatusTone(order.status)}
          />
        </OvLabelledField>
        <OvLabelledField label={t('orders.tableHeaders.paymentStatus')}>
          <OvStatusChip
            label={order.payment_status}
            tone={getPaymentStatusTone(order.payment_status)}
          />
        </OvLabelledField>
        <OvLabelledField label={t('orders.tableHeaders.fulfillmentStatus')}>
          <OvStatusChip
            label={order.fulfillment_status}
            tone={getFulfillmentStatusTone(order.fulfillment_status)}
          />
        </OvLabelledField>
        <OvLabelledField label={t('orders.tableHeaders.delivery')}>
          <OvOrderDeliveryStatus order={order} />
        </OvLabelledField>
      </StatusRail>
    </OvRecordDetailsHeader>
  );
};

export default OvOrderDetailsHeader;

const Amount = styled.span`
  font-size: ${Variables.fontSizes.XLARGE};
  font-weight: bold;
`;

const Tag = styled.span`
  padding: 0.125rem 0.375rem;
  border-radius: ${Variables.borderRadius.SMALL};
  background-color: ${Colours.OV_BORDER_COLOR_V3};
  color: ${Colours.OV_BASE};
`;

const StatusRail = styled.dl`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1.5fr;
  gap: 0.75rem 1.25rem;
  margin: 0.875rem 0 0;
  padding-top: 0.875rem;
  border-top: 1px solid ${Colours.OV_BORDER_COLOR_V3};

  > div {
    padding-left: 1.125rem;
    border-left: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  }

  > div:first-child {
    padding-left: 0;
    border-left: 0;
  }

  @media (max-width: ${breakpoints.md}) {
    gap: 0.5rem;

    > div {
      padding-left: 0.5rem;
    }
  }

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr 1fr;

    > div {
      padding-left: 0;
      border-left: 0;
    }
  }
`;
